/**
 * Local extensions until @wealthlane/api-services regenerates ParticipantType / EntityDetails.
 */

export const ParticipantTypeValue = {
  Individual: 1,
  Entity: 2,
} as const;

export type ParticipantTypeValue =
  (typeof ParticipantTypeValue)[keyof typeof ParticipantTypeValue];

export type EntityDetailsDto = {
  legalName?: string | null;
  entityType?: string | null;
  registrationNumber?: string | null;
  taxIdType?: string | null;
  taxId?: string | null;
  formationDate?: string | null;
  website?: string | null;
  notes?: string | null;
  legalNameErrorMessage?: string | null;
};

export type EntityParticipantFormValues = {
  employeeId: string;
  email: string;
  legalName: string;
  entityType: string;
  registrationNumber: string;
  taxIdType: string;
  taxId: string;
  formationDate: string;
  website: string;
  notes: string;
  employmentAddressLine1: string;
  employmentAddressLine2: string;
  employmentCountryId: string | null;
  employmentStateId: string | null;
  employmentStateText: string;
  employmentCityText: string;
  employmentZipCode: string;
  isOtherCountry: boolean;
  employeeStatus?: string;
  participantId?: string;
  id?: string;
};

export const emptyEntityParticipantFormValues = (): EntityParticipantFormValues => ({
  employeeId: '',
  email: '',
  legalName: '',
  entityType: '',
  registrationNumber: '',
  taxIdType: '',
  taxId: '',
  formationDate: '',
  website: '',
  notes: '',
  employmentAddressLine1: '',
  employmentAddressLine2: '',
  employmentCountryId: null,
  employmentStateId: null,
  employmentStateText: '',
  employmentCityText: '',
  employmentZipCode: '',
  isOtherCountry: false,
});

export function isEntityParticipantType(value: unknown): boolean {
  if (value === ParticipantTypeValue.Entity || value === 'Entity' || value === 'entity') {
    return true;
  }
  if (typeof value === 'string' && value.toLowerCase() === 'entity') {
    return true;
  }
  return Number(value) === ParticipantTypeValue.Entity;
}

export function mapApiToEntityFormValues(data: any): EntityParticipantFormValues {
  const details: EntityDetailsDto = data?.entityDetails ?? {};
  return {
    ...emptyEntityParticipantFormValues(),
    participantId: data?.participantId ?? data?.id ?? '',
    id: data?.participantId ?? data?.id ?? '',
    employeeId: data?.employeeId ?? data?.employeeID ?? '',
    email: data?.workEmail?.trim() || data?.email?.trim() || '',
    legalName: details.legalName ?? data?.legalName ?? '',
    entityType: details.entityType ?? '',
    registrationNumber: details.registrationNumber ?? '',
    taxIdType: details.taxIdType ?? '',
    taxId: details.taxId ?? '',
    formationDate: details.formationDate
      ? String(details.formationDate).slice(0, 10)
      : '',
    website: details.website ?? '',
    notes: details.notes ?? '',
    employmentAddressLine1: data?.employmentAddressLine1 ?? '',
    employmentAddressLine2: data?.employmentAddressLine2 ?? '',
    employmentCountryId: data?.employmentCountryId ?? null,
    employmentStateId: data?.employmentStateId ?? null,
    employmentStateText: data?.employmentStateText ?? '',
    employmentCityText: data?.employmentCityText ?? '',
    employmentZipCode: data?.employmentZipCode ?? '',
    isOtherCountry: Boolean(data?.isOtherCountry),
    employeeStatus: data?.employeeStatus ?? 'ACTIVE',
  };
}

export function buildEntityParticipantPayload(
  values: EntityParticipantFormValues,
  mode: 'add' | 'edit'
): Record<string, unknown> {
  const participantId = values.participantId?.trim() || values.id?.trim() || null;

  const payload: Record<string, unknown> = {
    participantType: ParticipantTypeValue.Entity,
    employeeId: values.employeeId?.trim(),
    email: values.email?.trim() || '',
    employeeStatus: values.employeeStatus || 'ACTIVE',
    importType: mode,
    employmentAddressLine1: values.employmentAddressLine1 || null,
    employmentAddressLine2: values.employmentAddressLine2 || null,
    employmentCountryId: values.employmentCountryId || null,
    employmentStateId: values.isOtherCountry ? null : values.employmentStateId || null,
    employmentStateText: values.isOtherCountry ? values.employmentStateText || null : null,
    employmentCityText: values.employmentCityText || null,
    employmentZipCode: values.employmentZipCode || null,
    entityDetails: {
      legalName: values.legalName?.trim(),
      entityType: values.entityType || null,
      registrationNumber: values.registrationNumber || null,
      taxIdType: values.taxIdType || null,
      taxId: values.taxId || null,
      formationDate: values.formationDate || null,
      website: values.website || null,
      notes: values.notes || null,
    } satisfies EntityDetailsDto,
  };

  if (mode === 'edit' && participantId) {
    // Individual edit sends both fields; backend UI validation looks up by `id`.
    payload.participantId = participantId;
    payload.id = participantId;
  }

  return payload;
}
